import React, { useMemo } from "react"
import { DateTime } from "luxon"
import { DatesSetArg, EventClickArg, EventContentArg } from "@fullcalendar/core"
import { StandardCalendar } from "../../../../../shared/components/calendar/StandardCalendar"
import { DayView } from "./DayView"
import { ErrorBoundary } from "react-error-boundary"
import { InlineFallbackContents } from "../../../../../shared/components/errors/InlineErrorBoundary"
import { makeStyles } from "tss-react/mui"
import { MenuCalendarEvent } from "../pages/MenuCalendar"

interface CalendarComponentProps {
    events: MenuCalendarEvent[]
    onDateClick: (dateStr: string, allDay: boolean) => void
    onEventClick: (eventInfo: { id: number; date: string }) => void
    setStartDate: (date: DateTime) => void
    setEndDate: (date: DateTime) => void
}

const useStyles = makeStyles()((theme) => ({
    errorBoundary: {
        padding: "10%",
    },
}))

const CalendarComponent: React.FC<CalendarComponentProps> = ({
    events,
    onDateClick,
    onEventClick,
    setStartDate,
    setEndDate,
}) => {
    const { classes } = useStyles()

    const handleDateClick = (arg: any) => {
        onDateClick(arg.dateStr, arg.allDay)
    }

    const handleEventClick = (clickInfo: EventClickArg) => {
        const eventId = Number(clickInfo.event.id)
        onEventClick({
            id: eventId,
            date: clickInfo.event.startStr, // or `clickInfo.event.start.toISOString()` if need the ISO string
        })
    }

    const handleDatesSet = (datesSetInfo: DatesSetArg) => {
        setStartDate(DateTime.fromJSDate(datesSetInfo.start))
        setEndDate(DateTime.fromJSDate(datesSetInfo.end))
    }

    const defaultEventContent = (arg: EventContentArg) => (
        <ErrorBoundary
            fallback={
                <InlineFallbackContents
                    errorMessageId="inline-error-boundary-calendar-event-message"
                    externalClasses={classes.errorBoundary}
                />
            }
        >
            <DayView event={arg.event} />
        </ErrorBoundary>
    )

    const baseEvents = useMemo(() => events.map((event) => ({ ...event, id: event.id.toString() })), [events])

    return (
        <StandardCalendar
            events={baseEvents}
            timeGrid={null}
            dayGrid={defaultEventContent}
            handleEventClick={handleEventClick}
            handleDatesSet={handleDatesSet}
            handleDateClick={handleDateClick}
        />
    )
}

export default CalendarComponent
